import { Box, Button, Chip, Divider } from "@mui/material";
import { grey } from "@mui/material/colors";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { QuickSendErrorContext } from "contexts/QuickSendError";
import { Dispatch, SetStateAction, useContext, useEffect, useRef } from "react";
import { PreviewMessage } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import { MAXIMUM_FOLLOWUP_ALLOWED } from "constants/templates";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import { trackEvent } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";

import FollowUpTab from "./FollowUpTab";
import styles from "./styles";

const DEFAULT_FOLLOWUP_DELAY_DAYS = 4;

interface IProps {
  emailMessages: PreviewMessage[];
  setEmailMessages: Dispatch<SetStateAction<PreviewMessage[]>>;
  draftTab: number;
  setDraftTab: Dispatch<SetStateAction<number>>;
  setSelectedTemplates: Dispatch<
    SetStateAction<{ [key in number]: Template | null }>
  >;
}

export default function SelectDraftsTab({
  emailMessages,
  setEmailMessages,
  draftTab,
  setDraftTab,
  setSelectedTemplates,
}: IProps) {
  const followups = emailMessages.slice(1, emailMessages?.length);
  const { followupTemplates } = useContext(OutreachTemplatesContext);
  const { errors } = useContext(QuickSendErrorContext);
  const { emailSettings } = useContext(OrganizationUserContext);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const savedDelayForOrder = (emailOrder: number): number => {
    const saved = emailSettings?.emailSettingTemplates?.find(
      (t) => t.emailOrder === emailOrder,
    )?.delayDays;
    return saved ?? DEFAULT_FOLLOWUP_DELAY_DAYS;
  };

  const handleAddMoreFollowup = () => {
    if (followups?.length === MAXIMUM_FOLLOWUP_ALLOWED) return;
    trackEvent("Add Another Follow Up Button Clicked", {
      Source: "Compose Email",
    });

    setEmailMessages((prev) => {
      const copy = makeDeepCopy(prev);
      const newOrder = copy.length;
      const defaultDelay = savedDelayForOrder(newOrder);
      if (followupTemplates?.length === 0) {
        copy.push({
          name: "Followup Default",
          body: "",
          subject: "Followup Default",
          contentType: TemplateContentType.FOLLOWUP,
          delayDays: defaultDelay,
        });
      } else {
        const newTemplate = followupTemplates[0];
        copy.push({
          ...newTemplate,
          delayDays: defaultDelay,
        });
        const newLength = copy.length;
        setSelectedTemplates((prev) => {
          const selectedCopy = { ...prev };
          selectedCopy[newLength - 1] = newTemplate;
          return selectedCopy;
        });
      }
      return copy;
    });

    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
      }
    }, 50);
  };

  const handleRemoveFollowUp = (index: number) => {
    trackEvent("Remove Follow Up Button Clicked", {
      "Follow Up Position": index,
      Source: "Compose Email",
    });
    setEmailMessages((prev: PreviewMessage[]) => {
      const copy = makeDeepCopy(prev);
      copy.splice(index, 1);
      return copy;
    });
    if (draftTab >= index) {
      setDraftTab((prev) => prev - 1);
    }
  };

  const handleDelayChange = (index: number, value: number) => {
    trackEvent("Follow Up Delay Changed", {
      "Follow Up Position": index,
      "Delay Days": value,
      Source: "Compose Email",
    });
    setEmailMessages((prev: PreviewMessage[]) => {
      const copy = makeDeepCopy(prev);
      if (copy[index]) {
        copy[index] = { ...copy[index], delayDays: value };
      }
      return copy;
    });
  };

  const switchToOutreach = (e: CustomEvent) => {
    e.preventDefault();
    setDraftTab(0);
  };

  const switchToFollowUp = (e: CustomEvent, index: number) => {
    e.preventDefault();
    setDraftTab(index);
  };

  useEffect(() => {
    setEmailMessages((prev) => {
      let changed = false;
      const next = prev.map((msg, idx) => {
        if (idx === 0) return msg;
        if (msg.delayDays == null) {
          changed = true;
          return { ...msg, delayDays: savedDelayForOrder(idx) };
        }
        return msg;
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailMessages.length, emailSettings, setEmailMessages]);

  return (
    <>
      <Box
        ref={scrollRef}
        sx={{
          display: "flex",
          alignItems: "end",
          overflowX: "auto",
          scrollBehavior: "smooth",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Chip
          label="Email"
          size="small"
          sx={[
            { flexShrink: 0 },
            draftTab === 0 ? styles.active : styles.inactive,
            errors.outreachBody === 0 && draftTab !== 0 && styles.error,
          ]}
          onClick={switchToOutreach}
        />

        {followups?.map((x, index) => {
          const tabIndex = index + 1;
          const delayDays = x.delayDays ?? DEFAULT_FOLLOWUP_DELAY_DAYS;
          return (
            <FollowUpTab
              key={index}
              tabIndex={tabIndex}
              days={delayDays}
              sx={[
                draftTab === tabIndex ? styles.active : styles.inactive,
                errors.outreachBody === tabIndex &&
                  draftTab !== tabIndex &&
                  styles.error,
              ]}
              onSelect={(e) => switchToFollowUp(e, tabIndex)}
              onDelayChange={(val) => handleDelayChange(tabIndex, val)}
              onRemove={() => handleRemoveFollowUp(tabIndex)}
            />
          );
        })}

        {followups?.length < MAXIMUM_FOLLOWUP_ALLOWED && (
          <Button
            size="small"
            sx={[styles.addButton, { flexShrink: 0 }]}
            onClick={handleAddMoreFollowup}
          >
            {followups?.length > 0 ? "Add + " : "Add Follow-Up +"}
          </Button>
        )}
      </Box>
      <Divider sx={{ mt: -0.1, backgroundColor: grey[200] }} />
    </>
  );
}
